import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { queryKeys } from '../lib/queryKeys';
import * as db from '../services/dbService';
import { createDefaultCategories } from '../data/defaultHabits';
import { Category, Habit, HabitType, MonthlyHabitData } from '../types';

export function useCategories(userId: string) {
  const qc = useQueryClient();
  const key = queryKeys.categories(userId);

  const { data: fetchedCats = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: async () => {
      const cats = await db.fetchCategories();
      if (cats.length === 0) {
        const initialCategories = createDefaultCategories();
        await db.syncCategories(initialCategories);
        return initialCategories;
      }
      return cats;
    },
    staleTime: 5 * 60 * 1000,
  });

  const categories: Category[] = isLoading ? [] : fetchedCats;

  const syncMutation = useMutation({
    mutationFn: (cats: Category[]) => db.syncCategories(cats),
    onMutate: async (newCats) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Category[]>(key);
      qc.setQueryData(key, newCats);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
  });

  /** Patch a single habit field in the DB without syncing the full categories tree. */
  const patchMutation = useMutation({
    mutationFn: ({ habitId, fields }: { habitId: string; fields: Parameters<typeof db.patchHabit>[1] }) =>
      db.patchHabit(habitId, fields),
    onMutate: async ({ habitId, fields }) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Category[]>(key);
      qc.setQueryData<Category[]>(key, prev =>
        (prev ?? []).map(cat => ({
          ...cat,
          habits: cat.habits.map(h =>
            h.id === habitId
              ? { ...h, ...fields, scheduledTime: (fields.scheduledTime ?? undefined) } as Habit
              : h
          ),
        })),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
  });

  /** Update a single habit field in cache and persist it. */
  const patchHabit = useCallback(
    async (habitId: string, fields: Parameters<typeof db.patchHabit>[1]) => {
      await patchMutation.mutateAsync({ habitId, fields });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId],
  );

  const deleteMutation = useMutation({
    mutationFn: (habitId: string) => db.deleteHabit(habitId),
    onMutate: async (habitId) => {
      await qc.cancelQueries({ queryKey: key });
      await qc.cancelQueries({ queryKey: ['entries', userId] });
      const previous = qc.getQueryData<Category[]>(key);
      qc.setQueryData<Category[]>(key, old =>
        (old ?? []).map(cat => ({ ...cat, habits: cat.habits.filter(h => h.id !== habitId) }))
      );
      qc.setQueriesData<Record<string, MonthlyHabitData>>({ queryKey: ['entries', userId] }, (old) => {
        if (!old) return old;
        const next = { ...old };
        Object.keys(next).forEach((entryKey) => {
          if (entryKey.startsWith(`${habitId}-`)) delete next[entryKey];
        });
        return next;
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
  });

  /** Apply an updater to the cached categories and sync to DB. */
  const updateCats = useCallback((updater: (prev: Category[]) => Category[]) => {
    const current = qc.getQueryData<Category[]>(key) ?? createDefaultCategories();
    syncMutation.mutate(updater(current));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addHabit = useCallback(async (
    name: string,
    type: HabitType,
    color: string,
    temporary?: boolean,
    scheduledTime?: string,
  ) => {
    const current = qc.getQueryData<Category[]>(key) ?? createDefaultCategories();
    const duplicate = current.some(cat =>
      cat.habits.some(h => h.name.trim().toLowerCase() === name.trim().toLowerCase()),
    );
    if (duplicate) {
      throw new Error('DUPLICATE_NAME');
    }
    const id = crypto.randomUUID();
    const next = current.map((cat, i) =>
      i === 0
        ? {
            ...cat,
            habits: [
              ...cat.habits,
              {
                id,
                name,
                type,
                category: cat.id,
                color,
                ...(temporary ? { temporary: true } : {}),
                ...(scheduledTime ? { scheduledTime } : {}),
              },
            ],
          }
        : cat,
    );
    await syncMutation.mutateAsync(next);
    return id;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renameHabit = useCallback((habitId: string, name: string) => {
    return patchHabit(habitId, { name });
  }, [patchHabit]);

  const updateHabitType = useCallback((habitId: string, type: HabitType) => {
    return patchHabit(habitId, { type });
  }, [patchHabit]);

  const updateHabitColor = useCallback((habitId: string, color: string) => {
    return patchHabit(habitId, { color });
  }, [patchHabit]);

  const updateHabitScheduledTime = useCallback((habitId: string, scheduledTime: string | undefined) => {
    return patchHabit(habitId, { scheduledTime: scheduledTime ?? null });
  }, [patchHabit]);

  const reorderHabits = useCallback((fromId: string, toId: string) => {
    updateCats(prev =>
      prev.map(cat => {
        const habits = [...cat.habits];
        const fi = habits.findIndex(h => h.id === fromId);
        const ti = habits.findIndex(h => h.id === toId);
        if (fi === -1 || ti === -1) return cat;
        const [moved] = habits.splice(fi, 1);
        habits.splice(ti, 0, moved);
        return { ...cat, habits };
      })
    );
  }, [updateCats]);

  return {
    categories,
    isLoading,
    /** Directly set categories (used by undo). */
    syncCategories: (cats: Category[]) => syncMutation.mutate(cats),
    deleteHabit: (habitId: string) => deleteMutation.mutate(habitId),
    addHabit,
    renameHabit,
    updateHabitType,
    updateHabitColor,
    updateHabitScheduledTime,
    reorderHabits,
  };
}
